import React, { createContext, useContext, useState, useCallback } from 'react';
import apiClient from '../api/client';

const ReplayContext = createContext(null);

export function ReplayProvider({ children }) {
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replaySession, setReplaySession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Start a replay session from a report JSON
   * @param {Object} reportJson - The parsed report JSON data
   * @returns {Promise<Object>} The created session data
   */
  const startReplay = useCallback(async (reportJson) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiClient.post('/api/decks/replay', {
        report_json: reportJson
      });
      
      const session = response.data;
      setReplaySession(session);
      setIsReplayMode(true);
      
      // Store in sessionStorage for persistence across page refreshes
      sessionStorage.setItem('replay_session', JSON.stringify(session));
      
      return session;
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to start replay session';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Exit replay mode and delete the session
   */
  const exitReplay = useCallback(async () => {
    if (!replaySession?.session_id) {
      setIsReplayMode(false);
      setReplaySession(null);
      sessionStorage.removeItem('replay_session');
      return;
    }

    try {
      await apiClient.delete(`/api/decks/replay/${replaySession.session_id}`);
    } catch (err) {
      // Session might have expired, that's fine
      console.warn('Failed to delete replay session:', err);
    } finally {
      setIsReplayMode(false);
      setReplaySession(null);
      sessionStorage.removeItem('replay_session');
    }
  }, [replaySession]);

  /**
   * Restore replay session from sessionStorage (on mount)
   */
  const restoreSession = useCallback(() => {
    const stored = sessionStorage.getItem('replay_session');
    if (stored) {
      try {
        const session = JSON.parse(stored);
        // Check if session might still be valid (simple time check)
        const createdAt = new Date(session.created_at || session.deck_title);
        const now = new Date();
        const minutesElapsed = (now - createdAt) / (1000 * 60);
        
        if (minutesElapsed < session.expires_in_minutes) {
          setReplaySession(session);
          setIsReplayMode(true);
        } else {
          // Session likely expired
          sessionStorage.removeItem('replay_session');
        }
      } catch (e) {
        sessionStorage.removeItem('replay_session');
      }
    }
  }, []);

  /**
   * Get the replay-aware API URL prefix
   * @param {string} baseUrl - The base API URL
   * @returns {string} The URL, possibly modified for replay mode
   */
  const getReplayUrl = useCallback((baseUrl) => {
    if (isReplayMode && replaySession?.session_id) {
      // For replay mode, use the replay endpoints
      return `/api/decks/replay/${replaySession.session_id}${baseUrl}`;
    }
    return baseUrl;
  }, [isReplayMode, replaySession]);

  const value = {
    isReplayMode,
    replaySession,
    loading,
    error,
    startReplay,
    exitReplay,
    restoreSession,
    getReplayUrl,
  };

  return (
    <ReplayContext.Provider value={value}>
      {children}
    </ReplayContext.Provider>
  );
}

export function useReplay() {
  const context = useContext(ReplayContext);
  if (!context) {
    throw new Error('useReplay must be used within ReplayProvider');
  }
  return context;
}
